// 대시보드 (투자 종합현황) — 모바일
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, Cell, ResponsiveContainer,
} from 'recharts';
import {
  fetchAccounts,
  fetchPortfolioSummary,
  fetchPeriodChart,
  fetchPortfolioHoldings,
  axiosInstance,
} from '../api/mobileApi';
import AccountChips from '../components/AccountChips';
import PeriodTabs   from '../components/PeriodTabs';
import MetricCard   from '../components/MetricCard';
import '../styles/mobile.css';

// ── 상수 ──────────────────────────────────────────────────────
const NOW_YEAR  = new Date().getFullYear();
const NOW_YM    = `${NOW_YEAR}${String(new Date().getMonth() + 1).padStart(2, '0')}`;
const WEEKDAYS  = ['일', '월', '화', '수', '목', '금', '토'];
const todayStr  = () => new Date().toISOString().slice(0, 10);

// ── 포맷 헬퍼 ─────────────────────────────────────────────────
const fNum    = v => (v == null ? '-' : Math.round(Number(v)).toLocaleString('ko-KR'));
const fCard   = v => {
  if (v == null) return '-';
  const sign = v < 0 ? '-' : '';
  const n = Math.abs(v);
  if (n >= 1e8) return `${sign}${(n / 1e8).toFixed(1)}억`;
  if (n >= 1e4) return `${sign}${Math.round(n / 1e4)}만`;
  return `${sign}${Math.round(n).toLocaleString('ko-KR')}`;
};
const fSign   = v => (Number(v) > 0 ? '+' : '');
const clsCls  = v => (Number(v) > 0 ? 'pos' : Number(v) < 0 ? 'neg' : '');
const fYAxis  = v => {
  const n = Math.abs(v);
  if (n >= 1e8) return `${(v / 1e8).toFixed(0)}억`;
  if (n >= 1e4) return `${(v / 1e4).toFixed(0)}만`;
  return String(Math.round(v));
};

const formatDay = dateStr => {
  const d   = new Date(dateStr);
  const dow = WEEKDAYS[d.getUTCDay()];
  const [y, m, day] = dateStr.split('-');
  return `${y}년 ${m}월 ${day}일 (${dow})`;
};

// ── 기간 카드 컴포넌트 ─────────────────────────────────────────
function PeriodCard({ label, sub, value, alwaysPos, isTotal, rate }) {
  const v    = value ?? null;
  const cls  = alwaysPos ? 'pos' : (v != null ? clsCls(v) : '');
  const valCls = isTotal ? 'mb-period-card-value total-val' : `mb-period-card-value${cls ? ` ${cls}` : ''}`;
  const showExact = v != null && Math.abs(v) >= 10000;
  const rateText = rate != null ? `${fSign(rate)}${Number(rate).toFixed(2)}%` : null;

  return (
    <div className={`mb-period-card${isTotal ? ' total' : ''}`}>
      <div className="mb-period-card-left">
        <div className="mb-period-card-label">{label}</div>
        {sub && <div className="mb-period-card-sub">{sub}</div>}
      </div>
      <div className="mb-period-card-right">
        <div className={valCls}>
          {v == null ? '-' : `${v > 0 ? '+' : ''}${fCard(v)}원`}
        </div>
        {rateText && (
          <div className={`mb-period-card-rate${cls ? ` ${cls}` : ''}`}>
            {rateText}
          </div>
        )}
        {showExact && (
          <div className="mb-period-card-exact">
            {fSign(v)}{fNum(v)}원
          </div>
        )}
      </div>
    </div>
  );
}

// ── 차트 툴팁 ─────────────────────────────────────────────────
const ChartTooltip = ({ type }) => ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const get = key => { const e = payload.find(p => p.dataKey === key); return e ? Number(e.value ?? 0) : null; };
  const sell  = get('sell_profit') ?? 0;
  const div   = get('dividend')    ?? 0;
  const evalP = get('eval_profit');
  const fmt   = v => `${fSign(v)}${fNum(v)}원`;
  const clr   = v => (v ?? 0) >= 0 ? '#e53935' : '#1565c0';

  return (
    <div style={{
      background: '#fff', border: '1px solid #e4e8f0',
      borderRadius: 8, padding: '8px 12px',
      fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: '#1a1a2e' }}>{label}</div>
      {type === 'stock' ? (
        <>
          {evalP != null && (
            <div style={{ color: clr(evalP) }}>당일평가수익: {fmt(evalP)}</div>
          )}
          <div style={{ color: clr(sell) }}>매도확정: {fmt(sell)}</div>
        </>
      ) : (
        <>
          <div style={{ color: clr(sell) }}>매도확정: {fmt(sell)}</div>
          <div style={{ color: '#f5a623' }}>분배금/이자: {fmt(div)}</div>
          {evalP != null && (
            <div style={{ color: clr(evalP), fontWeight: 600 }}>평가손익: {fmt(evalP)}</div>
          )}
        </>
      )}
    </div>
  );
};

// ── 차트 렌더 ─────────────────────────────────────────────────
function PeriodChart({ chartData, chartType }) {
  if (!chartData?.length) {
    return (
      <div className="mb-empty" style={{ padding: '20px 16px' }}>
        {chartType === 'stock'
          ? '오늘 시세수집 후 차트가 생성됩니다.'
          : '해당 기간의 데이터가 없습니다.'}
      </div>
    );
  }

  // 오늘(stock): 종목별 막대, 가로스크롤
  if (chartType === 'stock') {
    const barW  = 56;
    const chartW = Math.max(window.innerWidth - 32, chartData.length * barW + 80);
    return (
      <div className="mb-chart-wrap">
        {chartData.length > 6 && (
          <div className="mb-chart-hint">← 좌우로 스크롤하세요</div>
        )}
        <ComposedChart
          width={chartW} height={260}
          data={chartData}
          margin={{ top: 8, right: 8, left: 8, bottom: 40 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" angle={-45} textAnchor="end"
                 height={52} interval={0} tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={fYAxis} tick={{ fontSize: 10 }} width={46} />
          <Tooltip content={ChartTooltip({ type: 'stock' })} />
          <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
          <Bar dataKey="eval_profit" name="당일평가수익" maxBarSize={24}>
            {chartData.map((d, i) => (
              <Cell key={i} fill={(d.eval_profit ?? 0) >= 0 ? '#e53935' : '#1565c0'} />
            ))}
          </Bar>
          <Bar dataKey="sell_profit" name="매도확정손익" fill="#f5a623" maxBarSize={24} />
          <Legend wrapperStyle={{ fontSize: 10, paddingTop: 4 }} />
        </ComposedChart>
      </div>
    );
  }

  // 이번달(daily): 일별 라인 + 막대
  if (chartType === 'daily') {
    const interval = chartData.length > 15 ? 4 : 0;
    return (
      <div className="mb-chart-wrap">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={interval}
                   angle={-45} textAnchor="end" height={40} />
            <YAxis tickFormatter={fYAxis} tick={{ fontSize: 9 }} width={44} />
            <Tooltip content={ChartTooltip({ type: 'daily' })} />
            <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
            <Bar dataKey="sell_profit" name="매도확정손익" maxBarSize={10}>
              {chartData.map((d, i) => (
                <Cell key={i} fill={d.sell_profit >= 0 ? '#e53935' : '#1565c0'} />
              ))}
            </Bar>
            <Bar dataKey="dividend" name="분배금/이자" fill="#f5a623" maxBarSize={10} />
            <Line dataKey="eval_profit" name="기간평가수익" stroke="#1a237e"
                  strokeWidth={2} dot={{ r: 2 }} type="monotone" />
            <Legend wrapperStyle={{ fontSize: 10 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // 올해/직접설정(monthly): 월별 막대+라인
  return (
    <div className="mb-chart-wrap">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={fYAxis} tick={{ fontSize: 9 }} width={44} />
          <Tooltip content={ChartTooltip({ type: 'monthly' })} />
          <ReferenceLine y={0} stroke="#ccc" strokeWidth={1} />
          <Bar dataKey="sell_profit" name="매도확정손익" maxBarSize={22}>
            {chartData.map((d, i) => (
              <Cell key={i}
                    fill={d.sell_profit > 0 ? '#e53935' : d.sell_profit < 0 ? '#1565c0' : '#ccc'} />
            ))}
          </Bar>
          <Bar dataKey="dividend" name="분배금/이자" fill="#f5a623" maxBarSize={22} />
          {chartData.some(d => d.eval_profit !== null) && (
            <Line dataKey="eval_profit" name="평가손익"
                  stroke="#1a237e" strokeWidth={2}
                  dot={{ r: 2 }} connectNulls={false} type="monotone" />
          )}
          <Legend wrapperStyle={{ fontSize: 10 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}


// ════════════════════════════════════════════════════════════
// 메인 컴포넌트
// ════════════════════════════════════════════════════════════
export default function DashboardPage() {
  const navigate = useNavigate();

  // ── 계좌 ──────────────────────────────────────────────────
  const [allAccounts,   setAllAccounts]   = useState([]);

  // 상세 페이지 이동 후 복귀해도 선택 상태 유지 (sessionStorage)
  const [selectedAccts, setSelectedAccts] = useState(() => {
    try {
      const s = sessionStorage.getItem('mb_selected_accts');
      return s ? JSON.parse(s) : [];
    } catch { return []; }
  });

  useEffect(() => {
    sessionStorage.setItem('mb_selected_accts', JSON.stringify(selectedAccts));
  }, [selectedAccts]);

  // ── 기간 ──────────────────────────────────────────────────
  const [period,        setPeriod]        = useState('year');
  const [navDate,       setNavDate]       = useState(todayStr());
  const [navYm,         setNavYm]         = useState(NOW_YM);
  const [navYear,       setNavYear]       = useState(NOW_YEAR);
  const [customStart,   setCustomStart]   = useState('');
  const [customEnd,     setCustomEnd]     = useState('');
  const [customQueried, setCustomQueried] = useState(false);

  // ── 데이터 ────────────────────────────────────────────────
  const [summary,     setSummary]     = useState(null);
  const [periodChart, setPeriodChart] = useState(null);
  const [holdings,    setHoldings]    = useState(null);
  const [minBuyYear,  setMinBuyYear]  = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);

  // ── 파생값 ────────────────────────────────────────────────
  const acctParam = useMemo(() => selectedAccts.join(','), [selectedAccts]);

  const periodParams = useMemo(() => {
    if (period === 'today')  return { period: 'today', date: navDate };
    if (period === 'month')  return { period: 'month', ym: navYm };
    if (period === 'year') {
      if (navYear === NOW_YEAR) return { period: 'year' };
      return { period: 'custom', start_ym: `${navYear}01`, end_ym: `${navYear}12` };
    }
    if (customQueried && customStart && customEnd)
      return { period: 'custom', start_ym: customStart, end_ym: customEnd };
    return { period: 'today', date: navDate };
  }, [period, navDate, navYm, navYear, customStart, customEnd, customQueried]);

  const showPeriod = period !== 'custom' || customQueried;

  const chartData = useMemo(() => {
    if (!periodChart?.labels?.length) return [];
    return periodChart.labels.map((label, i) => ({
      label,
      eval_profit:  periodChart.eval_profit?.[i]  ?? null,
      sell_profit:  periodChart.sell_profit?.[i]  ?? 0,
      dividend:     periodChart.dividend?.[i]      ?? 0,
    }));
  }, [periodChart]);

  const chartType   = periodChart?.chart_type || 'monthly';
  const donutTotal  = (summary?.period_profit ?? 0)
                    + (summary?.confirmed_profit ?? 0)
                    + (summary?.dividend ?? 0);

  // ── 계좌 로드 ─────────────────────────────────────────────
  useEffect(() => {
    fetchAccounts()
      .then(d => setAllAccounts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // ── 메인 데이터 로드 ──────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!showPeriod) return;
    setLoading(true);
    try {
      const [rs, rc, rh] = await Promise.allSettled([
        fetchPortfolioSummary(acctParam, periodParams),
        fetchPeriodChart(acctParam, periodParams),
        fetchPortfolioHoldings(acctParam),
      ]);
      if (rs.status === 'fulfilled' && rs.value) {
        setSummary(rs.value);
        if (rs.value.min_buy_year) setMinBuyYear(rs.value.min_buy_year);
      }
      if (rc.status === 'fulfilled') setPeriodChart(rc.value);
      if (rh.status === 'fulfilled') setHoldings(rh.value);
    } finally {
      setLoading(false);
    }
  }, [acctParam, periodParams, showPeriod]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── 실시간 새로고침 ───────────────────────────────────────
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const tickers = [...new Set((holdings || []).map(h => h.ticker).filter(Boolean))];
      if (!tickers.length) return;
      const res = await axiosInstance.get('/price/realtime', { params: { tickers: tickers.join(',') } });
      const priceMap = {};
      (res.data?.data || []).forEach(p => { priceMap[p.ticker] = p; });

      const newH = (holdings || []).map(h => {
        const p = priceMap[h.ticker];
        if (!p?.current_price) return h;
        const newEval   = p.current_price * (h.total_quantity || 0);
        const newProfit = newEval - (h.buy_amount || 0);
        const newRate   = h.buy_amount > 0 ? newProfit / h.buy_amount * 100 : 0;
        return { ...h,
          eval_amount:      Math.round(newEval * 100) / 100,
          eval_profit:      Math.round(newProfit * 100) / 100,
          eval_profit_rate: Math.round(newRate * 100) / 100,
          today_diff:       p.prev_diff ?? null,
          today_diff_rate:  p.diff_rate ?? null,
        };
      });
      setHoldings(newH);

      const totalBuy  = newH.reduce((s, h) => s + (h.buy_amount  || 0), 0);
      const totalEval = newH.reduce((s, h) => s + (h.eval_amount || 0), 0);
      const ep        = totalEval - totalBuy;
      const todayP    = newH.reduce((s, h) =>
        s + (h.today_diff != null ? h.today_diff * (h.total_quantity || 0) : 0), 0);
      setSummary(prev => prev ? {
        ...prev,
        total_eval:       totalEval,
        eval_profit:      Math.round(ep * 100) / 100,
        eval_profit_rate: Math.round((totalBuy > 0 ? ep / totalBuy * 100 : 0) * 100) / 100,
        today_profit:     todayP,
      } : prev);
    } catch (e) {
      console.error('시세 갱신 오류:', e);
    } finally {
      setRefreshing(false);
    }
  };

  // ── 기간 변경 ─────────────────────────────────────────────
  const handlePeriodChange = p => {
    setPeriod(p);
    if (p !== 'custom') setCustomQueried(false);
    if (p === 'today')  setNavDate(todayStr());
    if (p === 'month') {
      const now = new Date();
      setNavYm(`${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`);
    }
  };

  // ── 기간 레이블 (상세 페이지 전달용) ─────────────────────
  const periodLabel = useMemo(() => {
    if (period === 'today')  return formatDay(navDate);
    if (period === 'month')  return `${parseInt(navYm.slice(0,4))}년 ${parseInt(navYm.slice(4))}월`;
    if (period === 'year')   return `${navYear}년`;
    if (customQueried && customStart && customEnd) {
      const s = customStart, e = customEnd;
      return `${s.slice(0,4)}.${s.slice(4)} ~ ${e.slice(0,4)}.${e.slice(4)}`;
    }
    return '';
  }, [period, navDate, navYm, navYear, customStart, customEnd, customQueried]);

  // ── 종목별 상세 이동 ──────────────────────────────────────
  const goStockDetail = () => {
    navigate('/mobile/dashboard/stock-detail', {
      state: { accounts: selectedAccts },
    });
  };

  // ── 기간 손익 상세 이동 ───────────────────────────────────
  const goPeriodDetail = () => {
    navigate('/mobile/dashboard/period-detail', {
      state: { accounts: selectedAccts, periodParams, periodLabel },
    });
  };

  // ── 렌더 ──────────────────────────────────────────────────
  return (
    <>
      {/* 계좌 칩 */}
      <AccountChips
        accounts={allAccounts}
        selected={selectedAccts}
        onSelect={setSelectedAccts}
      />

      {/* ① 실시간 포트폴리오 현황 */}
      <div className="mb-section">
        <div className="mb-section-header">
          <span className="mb-section-title">① 실시간 포트폴리오 현황</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              className={`mb-refresh-btn${refreshing ? ' spinning' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
              title="실시간 시세 새로고침"
            >↻</button>
            <button className="mb-detail-link" onClick={goStockDetail}>
              종목별 상세 ›
            </button>
          </div>
        </div>

        {loading ? (
          <div className="mb-loading">불러오는 중...</div>
        ) : (
          <div className="mb-metrics-grid">
            <MetricCard
              label="총 투자금"
              value={summary?.total_buy}
              sub="매수금액 합계"
            />
            <MetricCard
              label="현재 평가금"
              value={summary?.total_eval}
              sub="실시간 시세 기준"
            />
            <MetricCard
              label="평가 손익"
              value={summary?.eval_profit}
              signed
              sub={summary ? `${fSign(summary.eval_profit_rate)}${Number(summary.eval_profit_rate ?? 0).toFixed(2)}%` : ''}
            />
            <MetricCard
              label="수익률"
              value={summary?.eval_profit_rate}
              unit="%"
              signed
              decimals={2}
              sub={summary?.today_profit != null
                ? `오늘 ${fSign(summary.today_profit)}${fCard(summary.today_profit)}원`
                : ''}
            />
          </div>
        )}
      </div>

      {/* 기간 탭 */}
      <div style={{ margin: '12px 0 0' }}>
        <PeriodTabs
          period={period}
          navDate={navDate}
          navYm={navYm}
          navYear={navYear}
          customStart={customStart}
          customEnd={customEnd}
          customQueried={customQueried}
          minBuyYear={minBuyYear}
          onPeriodChange={handlePeriodChange}
          onNavDateChange={setNavDate}
          onNavYmChange={setNavYm}
          onNavYearChange={setNavYear}
          onCustomStartChange={v => { setCustomStart(v); setCustomQueried(false); }}
          onCustomEndChange={v => { setCustomEnd(v); setCustomQueried(false); }}
          onCustomQuery={() => setCustomQueried(true)}
        />
      </div>

      {/* ② 기간별 손익 현황 */}
      {!showPeriod ? (
        <div className="mb-section">
          <div className="mb-empty">기간을 선택하고 조회 버튼을 눌러주세요.</div>
        </div>
      ) : loading ? (
        <div className="mb-section">
          <div className="mb-loading">불러오는 중...</div>
        </div>
      ) : (
        <div className="mb-section">
          <div className="mb-section-header">
            <span className="mb-section-title">② 기간별 손익 현황</span>
            <button className="mb-detail-link" onClick={goPeriodDetail}>
              기간 손익 상세 ›
            </button>
          </div>

          <div className="mb-period-stack">
            <PeriodCard
              label="기간 평가수익"
              sub={summary?.period_is_temp ? '실시간 시세 기준'
                : `${summary?.period_profit_label || ''} 집계 기준`}
              value={summary?.period_profit}
              rate={summary?.period_profit_rate}
            />
            <div className="mb-period-op">+</div>
            <PeriodCard
              label="매도 확정손익"
              sub="실현손익"
              value={summary?.confirmed_profit}
            />
            <div className="mb-period-op">+</div>
            <PeriodCard
              label="분배금 / 이자"
              sub="수령액"
              value={summary?.dividend}
              alwaysPos
            />
            <hr className="mb-period-divider" />
            <PeriodCard
              label="기간 총수익"
              sub="평가 + 확정 + 분배"
              value={donutTotal}
              isTotal
            />
          </div>

          {/* 차트 */}
          <PeriodChart chartData={chartData} chartType={chartType} />
        </div>
      )}

      {/* 하단 여백 */}
      <div style={{ height: 16 }} />
    </>
  );
}
